import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, ChevronDown, Hash, Check, Settings, LogOut, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useChat } from "@/hooks/useChat";
import MessageBubble from "@/components/MessageBubble";
import MessageInput from "@/components/MessageInput";
import PinnedBar from "@/components/PinnedBar";
import TypingIndicator from "@/components/TypingIndicator";
import { getWidgetConfig, saveWidgetConfig, WIDGET_COLORS } from "@/lib/widgetConfig";
import { login, fetchRooms, fetchGroup, mediaUrl } from "@/lib/chatApi";
import { AVATARS } from "@/lib/avatars";
import { GroupEmbedList } from "@/components/GroupEmbedList";

const STORAGE_KEY = "ruangchat_user";
const readStored = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

function WidgetBubble({ open, onToggle, color, unread = 0 }) {
  return (
    <button
      data-testid="chat-widget-bubble"
      onClick={onToggle}
      style={{ backgroundColor: color }}
      className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl shadow-black/40 transition-all hover:scale-105"
    >
      {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
      {!open && unread > 0 && (
        <span
          data-testid="widget-unread-badge"
          className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-950"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );
}

function WidgetSettings({ cfg, updateCfg }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="widget-settings-toggle" className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800" title="Pengaturan tampilan">
          <Settings className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[10000] w-64 p-3 bg-slate-900 border-slate-800 space-y-4">
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Warna Aksen</p>
          <div className="flex flex-wrap gap-2">
            {WIDGET_COLORS.map((c) => (
              <button
                key={c.value}
                data-testid={`widget-color-${c.name}`}
                onClick={() => updateCfg({ color: c.value })}
                className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                  cfg.color === c.value ? "border-white scale-110" : "border-transparent"
                }`}
                style={{ backgroundColor: c.value }}
                title={c.name}
              />
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Posisi</p>
          <div className="grid grid-cols-2 gap-2">
            {["left", "right"].map((p) => (
              <button
                key={p}
                data-testid={`widget-position-${p}`}
                onClick={() => updateCfg({ position: p })}
                className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                  cfg.position === p ? "bg-slate-800 border-slate-600 text-white" : "border-slate-700 text-slate-400 hover:bg-slate-800"
                }`}
              >
                {p === "left" ? "Kiri Bawah" : "Kanan Bawah"}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GuestWidget({ onLogin, open, setOpen, cfg, updateCfg, initialRoom }) {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [targetRoom, setTargetRoom] = useState(null);
  const posClass = cfg.position === "left" ? "left-5 items-start" : "right-5 items-end";

  useEffect(() => {
    if (!initialRoom) return;
    let cancelled = false;
    fetchRooms()
      .then((rooms) => {
        if (cancelled) return;
        const p = String(initialRoom).toLowerCase();
        const match = rooms.find(
          (x) => x.id === initialRoom || (x.slug || "").toLowerCase() === p || x.name.toLowerCase() === p
        );
        if (match) setTargetRoom(match);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [initialRoom]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Masukkan nama dulu ya");
      return;
    }
    setLoading(true);
    try {
      const u = await login(username.trim(), AVATARS[0]);
      onLogin(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal masuk, coba lagi");
    } finally {
      setLoading(false);
    }
  };

  const roomName = targetRoom?.name || "RuangChat";

  return (
    <div className={`dark fixed bottom-5 z-[9999] flex flex-col gap-3 font-sans ${posClass}`}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            data-testid="chat-widget-panel"
            className="w-[92vw] sm:w-[384px] h-[560px] max-h-[80vh] flex flex-col rounded-3xl overflow-hidden bg-slate-950 border border-slate-800 shadow-2xl shadow-black/50"
          >
            <div className="h-14 px-4 flex items-center justify-between bg-slate-900/90 border-b border-slate-800 shrink-0">
              <div className="flex items-center gap-2 min-w-0 text-white">
                {targetRoom?.logo ? (
                  <img src={mediaUrl(targetRoom.logo)} alt={roomName} data-testid="widget-guest-logo" className="w-7 h-7 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: cfg.color }}>
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                )}
                <span data-testid="widget-guest-title" className="text-sm font-semibold truncate">{roomName}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <WidgetSettings cfg={cfg} updateCfg={updateCfg} />
                <button data-testid="widget-close-button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center p-6 gap-5 bg-slate-950 text-center">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden" style={{ backgroundColor: targetRoom?.logo ? "transparent" : cfg.color }}>
                {targetRoom?.logo ? (
                  <img src={mediaUrl(targetRoom.logo)} alt={roomName} className="w-full h-full object-cover" />
                ) : (
                  <MessageCircle className="w-7 h-7 text-white" />
                )}
              </div>
              <div>
                <h3 data-testid="widget-guest-heading" className="text-lg font-bold text-white">{roomName}</h3>
                <p className="text-sm text-slate-400 mt-1">Masukkan nama untuk mulai chat dengan admin.</p>
              </div>
              <form onSubmit={submit} className="w-full space-y-3">
                <input
                  data-testid="widget-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Nama Anda"
                  className="w-full px-4 py-3 rounded-xl bg-slate-800/70 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
                <button
                  data-testid="widget-login-button"
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: cfg.color }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-semibold transition-all disabled:opacity-60 hover:brightness-110"
                >
                  {loading ? "Menghubungkan..." : <>Mulai Chat <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <WidgetBubble open={open} onToggle={() => setOpen((o) => !o)} color={cfg.color} />
    </div>
  );
}

function AuthedWidget({ user, onLogout, initialRoom, lockRoom, groupId, open, setOpen, cfg, updateCfg }) {
  const chat = useChat(user, initialRoom, groupId);
  const {
    rooms, activeRoom, messages, pinned, presence, typingUsers, unread, totalUnread,
    canModerate, users, selectRoom, sendMessage, sendTyping, sendReaction, sendPin,
  } = chat;

  const effectiveLock = lockRoom || rooms.length <= 1;

  const [replyTo, setReplyTo] = useState(null);
  const bottomRef = useRef(null);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => scrollToBottom("auto"), 60);
  }, [open, activeRoom?.id, scrollToBottom]);

  useEffect(() => {
    scrollToBottom("smooth");
  }, [messages, scrollToBottom]);

  useEffect(() => setReplyTo(null), [activeRoom?.id]);

  const handleSend = (text, imageUrl) => {
    const reply = replyTo ? { id: replyTo.id, username: replyTo.username, text: replyTo.text || "" } : null;
    sendMessage(text, imageUrl, reply);
    setReplyTo(null);
  };

  const posClass = cfg.position === "left" ? "left-5 items-start" : "right-5 items-end";

  return (
    <div className={`dark fixed bottom-5 z-[9999] flex flex-col gap-3 font-sans ${posClass}`}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            data-testid="chat-widget-panel"
            className="w-[92vw] sm:w-[384px] h-[560px] max-h-[80vh] flex flex-col rounded-3xl overflow-hidden bg-slate-950 border border-slate-800 shadow-2xl shadow-black/50"
          >
            <div className="h-14 px-4 flex items-center justify-between bg-slate-900/90 border-b border-slate-800 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={effectiveLock}>
                  <button data-testid="widget-room-switcher" className="flex items-center gap-2 min-w-0 text-white">
                    {activeRoom?.logo ? (
                      <img src={mediaUrl(activeRoom.logo)} alt={activeRoom.name} data-testid="widget-room-logo" className="w-7 h-7 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: cfg.color }}>
                        <Hash className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span className="text-sm font-semibold truncate">{activeRoom?.name || "RuangChat"}</span>
                    {!effectiveLock && <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                  </button>
                </DropdownMenuTrigger>
                {!effectiveLock && (
                  <DropdownMenuContent align="start" className="z-[10000] w-56 bg-slate-900 border-slate-800">
                    {rooms.map((r) => (
                      <DropdownMenuItem
                        key={r.id}
                        data-testid={`widget-room-${r.id}`}
                        onClick={() => selectRoom(r)}
                        className="flex items-center gap-2 text-slate-200 focus:bg-slate-800 cursor-pointer"
                      >
                        <Hash className="w-4 h-4 text-slate-500" />
                        <span className="flex-1 truncate">{r.name}</span>
                        {r.id === activeRoom?.id && <Check className="w-4 h-4 text-indigo-400" />}
                        {r.id !== activeRoom?.id && unread[r.id] > 0 && (
                          <span className="min-w-4 h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ backgroundColor: cfg.color }}>
                            {unread[r.id]}
                          </span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>

              <div className="flex items-center gap-1.5">
                <span className="text-xs text-emerald-400 font-medium">{presence.length} online</span>
                <WidgetSettings cfg={cfg} updateCfg={updateCfg} />
                <button data-testid="widget-logout-button" onClick={onLogout} title="Keluar" className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800">
                  <LogOut className="w-4 h-4" />
                </button>
                <button data-testid="widget-close-button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <PinnedBar pinned={pinned} onUnpin={(id) => sendPin(id, false)} accent={cfg.color} canModerate={canModerate} />

            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-950">
              {messages.length === 0 && (
                <div className="h-full flex items-center justify-center text-xs text-slate-500">Belum ada pesan. Sapa dulu yuk!</div>
              )}
              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  mine={m.username === user.username}
                  currentUser={user.username}
                  onReply={setReplyTo}
                  onReact={sendReaction}
                  onPin={canModerate ? (msg) => sendPin(msg.id, !msg.pinned) : undefined}
                />
              ))}
              <div ref={bottomRef} />
            </div>

            <TypingIndicator users={typingUsers} />

            {activeRoom && (
              <MessageInput
                onSend={handleSend}
                onTyping={sendTyping}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                users={users}
                compact
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <WidgetBubble open={open} onToggle={() => setOpen((o) => !o)} color={cfg.color} unread={totalUnread} />
    </div>
  );
}

export function FloatingChatWidget({ user: userProp = null, initialRoom = null, lockRoom = false, groupId = null }) {
  const [user, setUser] = useState(() => userProp || readStored());
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(getWidgetConfig());

  const updateCfg = (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    saveWidgetConfig(next);
  };

  const handleLogin = (u) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    setUser(u);
  };
  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
  };

  if (!user) {
    return <GuestWidget onLogin={handleLogin} open={open} setOpen={setOpen} cfg={cfg} updateCfg={updateCfg} initialRoom={initialRoom} />;
  }
  return (
    <AuthedWidget
      user={user}
      onLogout={handleLogout}
      initialRoom={initialRoom}
      lockRoom={lockRoom}
      groupId={groupId}
      open={open}
      setOpen={setOpen}
      cfg={cfg}
      updateCfg={updateCfg}
    />
  );
}

// Embed-only page: renders ONLY the floating bubble + popup on a transparent
// background so it can be dropped into any website via an <iframe>.
export function WidgetEmbed() {
  const params = new URLSearchParams(window.location.search);
  const room = params.get("room");
  const lock = params.get("lock") === "1" || params.get("lock") === "true";
  const group = params.get("group");

  useEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, []);

  return <FloatingChatWidget initialRoom={room} lockRoom={lock} groupId={group} />;
}

// Instruction/guide page (NOT for embedding) with copy-paste iframe snippets.
export function EmbedGuide() {
  const origin = window.location.origin;
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs font-semibold mb-6">
          Panduan Sematkan Widget
        </span>
        <h1 className="text-3xl md:text-4xl font-extrabold text-white font-heading tracking-tight">
          Tempel tombol chat di website Anda
        </h1>
        <p className="text-slate-400 mt-4 text-sm md:text-base">
          Halaman <code className="text-indigo-300">/widget</code> hanya menampilkan tombol chat mengambang (transparan) — cocok untuk iframe. Tempel salah satu snippet sebelum tag &lt;/body&gt;:
        </p>

        <p className="text-slate-300 mt-8 mb-2 text-sm font-semibold text-left max-w-xl mx-auto">1. Semua ruang (default)</p>
        <pre className="mx-auto max-w-xl text-left text-xs bg-slate-900 border border-slate-800 rounded-2xl p-4 text-emerald-300 overflow-x-auto">
{`<iframe src="${origin}/widget"
  style="position:fixed;bottom:0;right:0;border:0;
  width:440px;height:660px;z-index:99999"
  allowtransparency="true"></iframe>`}
        </pre>

        <p className="text-slate-300 mt-8 mb-2 text-sm font-semibold text-left max-w-xl mx-auto">
          2. Ruang tertentu — pakai <code className="text-indigo-300">?room=&lt;nama/slug&gt;</code>, tambahkan <code className="text-indigo-300">&amp;lock=1</code> untuk mengunci ke ruang itu
        </p>
        <pre className="mx-auto max-w-xl text-left text-xs bg-slate-900 border border-slate-800 rounded-2xl p-4 text-emerald-300 overflow-x-auto">
{`<iframe src="${origin}/widget?room=bantuan&lock=1"
  style="position:fixed;bottom:0;right:0;border:0;
  width:440px;height:660px;z-index:99999"
  allowtransparency="true"></iframe>`}
        </pre>
        <p className="text-slate-500 mt-4 text-xs max-w-xl mx-auto">
          Slug ruang bawaan: umum, diskusi-bebas, teknologi, bantuan, komunitas. Pengunjung memasukkan nama langsung di dalam popup, lalu chat dengan admin. Admin membuka aplikasi utama di <code>/</code> (login sebagai <code>admin</code>).
        </p>
        <GroupEmbedList origin={origin} />
      </div>
      <FloatingChatWidget />
    </div>
  );
}
